import React from "react";
import { TabContent } from "../TabLayout";
import { ExamForm, ExamFormValues } from "./ExamForm";
import { CourseExamApi, courseExamsApi, ExamBody } from "api/course-exams";
import { deFormatDate, toTimeFormat, useToaster } from "utils";
import { ExamsPreview } from "./ExamsPreview";
import { useCourse, useCourseExams } from "api/hooks";
import { ToggleReadinessButton } from "components/molecules";
import { coursesStatusApi } from "api/coursesStatusApi";
import { PaziaBox } from "components/atoms";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";

interface ExamsStepProps {
  courseId: number;
}
export const ExamsStep = ({ courseId }: ExamsStepProps) => {
  const { showSuccess, showError } = useToaster();
  const { isLoading, data, refetch } = useCourseExams(courseId);
  const { loading: loadingCourse, data: course, invalidate } = useCourse(courseId);
  const [modalExam, setModalExam] = React.useState<CourseExamApi | null>(null);

  const onSubmit = async (data: ExamFormValues) => {
    try {
      const request: ExamBody = {
        id: data.id ?? undefined,
        examId: Number(data.examId),
        name: data.name,
        description: data.description,
        day: deFormatDate(data.day)!,
        timeFrom: toTimeFormat(+data.fromTime),
        timeTo: toTimeFormat(+data.toTime),
        courseSessionId: Number(data.session),
        placeType: Number(data.placeType),
        placeId: data.place ? Number(data.place) : undefined,
        trainerIds: (data.trainerIds ?? []).map(Number),
        payments: data.payments,
      };
      if (data.id) {
        await courseExamsApi.update(courseId, data.id, request);
        showSuccess("Egzamin został zaktualizowany.");
      } else {
        await courseExamsApi.create(courseId, request);
        showSuccess("Egzamin został dodany.");
      }
      refetch();
    } catch (e) {
      showError("Nie udało się dodać egzaminu.");
      console.error(e);
    }
  };

  if (loadingCourse) return <PaziaLoader position="center" />;
  return (
    <TabContent className="flex flex-col gap-4">
      <PaziaBox className="flex justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Egzaminy</h2>
          <div className="mb-2 text-sm">
            Dodaj i zarządzaj egzaminami dla kursu. <br /> Egzaminy są widoczne dla kursantów po zapisaniu się na kurs.
          </div>
          <ToggleReadinessButton
            isReady={Boolean(course?.data.examsReady)}
            onReady={coursesStatusApi.EXAMS_READY}
            onUnready={coursesStatusApi.EXAMS_UNREADY}
            courseId={courseId}
            readyText="Egzaminy zostały zatwierdzone"
            invalidate={async () => {
              invalidate();
            }}
          />
        </div>
        <div></div>
      </PaziaBox>
      <div className="flex justify-end">
        <PaziaButton type="primary" onClick={() => setModalExam({} as CourseExamApi)}>
          Dodaj egzamin
        </PaziaButton>
      </div>
      <div>
        <ExamsPreview
          courseId={courseId}
          loading={isLoading}
          exams={data?.data ?? []}
          onSuccessDelete={() => {
            refetch();
          }}
          onEdit={(e) => setModalExam(e)}
        />
      </div>
      <ExamModal onCloseRequest={() => setModalExam(null)} exam={modalExam} onSubmit={onSubmit} courseId={courseId} />
    </TabContent>
  );
};

interface ExamModalProps {
  onCloseRequest: () => void;
  exam: CourseExamApi | null;
  onSubmit: (data: ExamFormValues) => Promise<void>;
  courseId: number;
}

const ExamModal = ({ exam, onCloseRequest, onSubmit, courseId }: ExamModalProps) => {
  const onSubmitHandler = async (data: ExamFormValues) => {
    await onSubmit(data);
    onCloseRequest();
  };
  return (
    <PaziaModal id="exam-modal" onCloseRequest={onCloseRequest} isOpen={exam !== null} size="3xl">
      <ModalPadding>
        <ModalHeader>Konfiguruj egzamin</ModalHeader>
        {exam && (
          <ExamForm
            onSubmit={onSubmitHandler}
            courseId={courseId}
            exam={exam ?? ({} as CourseExamApi)}
            onCancel={onCloseRequest}
          />
        )}
      </ModalPadding>
    </PaziaModal>
  );
};
